/**
 * Router.jsx - Main application routing configuration
 *
 * This file handles:
 * 1. Application route definitions
 * 2. Authentication state management
 * 3. Route protection based on auth status
 * 4. Redirect handling
 * 5. Initial application data loading
 */

import { Routes, Route, Navigate } from "react-router-dom";
import LoginPage from "../components/auth/login-page";
import Dashboard from "../pages/dashboard/dashboard";
import { useSelector, useDispatch } from "react-redux";
import authConfig from "../config/auth";
import { initApp, setMe } from "../store/slices/auth";
import { useEffect } from "react";
import { setTags } from "../store/slices/tags";
import { hasAccess } from "../services/access";
import Loader from "../components/loader/loader";
import { showError } from "../services/toast-service";
import { ACCESS_CONFIG } from "../components/strings";
import ResetPassword from "../components/auth/reset-password";
import { getMe, getBasicTags } from "../services/api-declaration";
import ForgotPasswordPage from "../components/auth/forgot-password";
import { loadCountryOptions } from "../store/slices/countryOptions";
import { useTranslation } from "react-i18next";

/**
 * Main router component responsible for handling application routing
 * and initial data loading based on authentication status
 *
 * @returns {JSX.Element} The application routes with auth-based protection
 */
const Router = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Get authentication state from Redux store
  const auth = useSelector((state) => state.auth);

  // Calculate login URL with redirect if needed
  const loginUrl = auth.redirect
    ? `/login?redirect=${auth.redirect}`
    : "/login";

  // Extract search parameters from URL
  const search = window.location.search;

  // Determine dashboard URL based on redirect parameter or default route
  const dashboardUrl =
    search.split("redirect=").length > 1
      ? search.split("redirect=").pop()
      : "dashboard/funnels";
  /**
   * Effect to handle authentication initialization on component mount
   *
   * This effect:
   * 1. Checks localStorage for an auth token
   * 2. If token exists, fetches the user data
   * 3. If no token or error, sets up redirect information
   */
  useEffect(() => {
    const initAuth = async () => {
      // Check for token in localStorage
      const storedToken = window.localStorage.getItem(
        authConfig.storageTokenKeyName
      );

      if (storedToken) {
        try {
          // If token exists, get user data
          const response = await getMe();
          dispatch(setMe(response.data.data));
        } catch (error) {
          console.log("error", error);
          showError("Authentication error!");
        }
      } else {
        // If no token, store current path for post-login redirect
        dispatch(
          initApp({
            value: `${window.location.pathname}${window.location.search}`,
            type: "redirect"
          })
        );
      }
    };

    initAuth();
  }, [dispatch]);

  /**
   * Effect to fetch basic tags data after successful authentication
   *
   * Only admin users can access tags functionality, so we only
   * fetch the tags data if the authenticated user has admin role
   */
  useEffect(() => {
    const fetchTags = async () => {
      try {
        // Fetch tags data from API
        const tagsResponse = await getBasicTags();
        const data = tagsResponse.data.data;

        // Store tags in Redux
        dispatch(setTags(data));
      } catch (error) {
        // Show error message using translation
        showError(
          t("toastMessage.errorInGetting", {
            what: "Basic Tags"
          })
        );
        console.log(error);
      }
    };
    // Only fetch tags if the user is from MARKETING_MANAGER_TIER
    if (hasAccess(ACCESS_CONFIG.MARKETING_MANAGER_TIER)) {
      fetchTags();
    }
    // eslint-disable-next-line
  }, [dispatch, auth?.user?.email, auth?.user?.role]);

  /**
   * Effect to load the country options used by the country dropdowns
   */
  useEffect(() => {
    if (!auth?.user?.email) return;
    dispatch(loadCountryOptions())
      .unwrap()
      .catch(() => {
        showError(t("toastMessage.errorInGetting", { what: "Countries" }));
      });
  }, [dispatch, t, auth?.user?.email]);
  /**
   * Render the application routes based on authentication state
   *
   * If auth is loading, display a loading indicator
   * Otherwise, render routes with protection based on auth status:
   * - Unauthenticated users can only access auth routes (/login, /forgot, /reset)
   * - Authenticated users are redirected to dashboard from auth routes
   * - Dashboard routes require authentication
   */
  return (
    <>
      {auth.loading ? (
        // Display loader while authentication state is being determined
        <Loader />
      ) : (
        <Routes>
          {/* Authentication Routes */}
          <Route
            path="/login"
            element={
              auth.user ? (
                // Redirect authenticated users to dashboard
                <Navigate to={`${dashboardUrl}`} replace />
              ) : (
                // Show login page for unauthenticated users
                <LoginPage />
              )
            }
          />
          <Route
            path="/forgot"
            element={
              auth.user ? (
                // Redirect authenticated users to dashboard
                <Navigate to="/dashboard/funnels" replace />
              ) : (
                // Show forgot password page for unauthenticated users
                <ForgotPasswordPage />
              )
            }
          />
          <Route
            path="/reset"
            element={
              auth.user ? (
                // Redirect authenticated users to dashboard
                <Navigate to="/dashboard/funnels" replace />
              ) : (
                // Show reset password page for unauthenticated users
                <ResetPassword />
              )
            }
          />

          {/* Protected Dashboard Routes */}
          <Route
            path="dashboard/*"
            element={
              auth.user ? (
                // Render dashboard for authenticated users
                <Dashboard />
              ) : (
                // Redirect unauthenticated users to login
                <Navigate to={`${loginUrl}`} replace />
              )
            }
          />

          {/* Catch-all Route */}
          <Route
            path="*"
            element={
              auth.user ? (
                // Redirect authenticated users to default dashboard
                <Navigate to="/dashboard/funnels" replace />
              ) : (
                // Redirect unauthenticated users to login
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      )}
    </>
  );
};
export default Router;
